/**
 * Structured clinical note (SOAP/DAP/BIRP) — generate, edit, sign, copy.
 * Rendered inside the Notes tab; the active format is picked by the parent's
 * format chips (SessionPanel) and each format runs its own summarize prompt
 * (tagged note_format in the prompt library) through the notes worker.
 * Section titles always come from the API (they're format/language specific
 * on the backend), never from frontend i18n. UI chrome only uses the
 * `progressNotes.*` i18n keys.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { Button } from "../../../components/ui/Button";
import { useProgressNotes, NoteFormat, NoteSection, ProgressNote } from "../hooks/useProgressNotes";

type Props = {
  format: NoteFormat;
  /** Selected summarize prompt id (prompt library); backend resolves the
   * newest prompt tagged with this format when absent. */
  promptId?: string;
  sessionId: string | undefined;
  sessionLang?: "en" | "hu";
  apiBase: string;
  ensureToken: () => Promise<string | null>;
};

export default function NotesPanel({ format, promptId, sessionId, sessionLang, apiBase, ensureToken }: Props) {
  const { t, i18n } = useTranslation();
  const { loadNotes, generate, saveNote, signNote, deleteNote } = useProgressNotes({
    apiBase,
    ensureToken,
    sessionId,
  });

  const [notes, setNotes] = useState<ProgressNote[]>([]);
  // Explicit therapist choice only; the effective language falls back to the
  // session's own language, then the UI language — generation never blocks
  // on picking one.
  const [langOverride, setLangOverride] = useState<"en" | "hu" | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftSections, setDraftSections] = useState<NoteSection[]>([]);
  // Identity of the note that draftSections was last derived from. Compared
  // during render (not in an effect) so the textarea contents are correct in
  // the very same commit the note becomes active — see "adjusting state
  // during rendering" (https://react.dev/learn/you-might-not-need-an-effect).
  const [draftSourceKey, setDraftSourceKey] = useState<string | undefined>(undefined);
  const [confirmSign, setConfirmSign] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(false);
  const [confirmDeleteSigned, setConfirmDeleteSigned] = useState(false);
  const [copied, setCopied] = useState(false);

  // Latest sessionId, for staleness checks in async callbacks: an in-flight
  // generate/save resolution from a previous session must never mutate the
  // current session's state (notes are PHI — cross-session bleed is a leak).
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Synchronous render-time reset on session switch (same commit — the old
  // session's notes must never be visible under the new session, and no
  // pending confirm/busy state may carry over).
  const [renderedSessionId, setRenderedSessionId] = useState(sessionId);
  if (sessionId !== renderedSessionId) {
    // Sync the ref here too, not only in the passive effect — otherwise a
    // stale promise resolving between this render and the effect flush would
    // still see the old id in the ref and slip one stale update through.
    sessionIdRef.current = sessionId;
    setRenderedSessionId(sessionId);
    setNotes([]);
    // Drop any explicit language choice — it belonged to the old session.
    // The effective language re-derives from the new session's lang (or the
    // UI language) automatically.
    setLangOverride("");
    setBusy(false);
    setError(null);
    setDraftSections([]);
    setDraftSourceKey(undefined);
    setConfirmSign(false);
    setConfirmRegenerate(false);
    setConfirmDeleteDraft(false);
    setConfirmDeleteSigned(false);
    setCopied(false);
  }

  // Effective note language: explicit choice > session's own language > UI
  // language. Always defined, so Generate is never blocked on a selector.
  const lang: "en" | "hu" =
    langOverride || sessionLang || (i18n.language?.startsWith("hu") ? "hu" : "en");

  useEffect(() => {
    if (!sessionId) return;
    const sid = sessionId;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await loadNotes();
        if (!cancelled && sid === sessionIdRef.current) setNotes(loaded);
      } catch (e: any) {
        if (!cancelled && sid === sessionIdRef.current) setError(e?.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const activeNote = useMemo(
    () => notes.find((n) => n.format === format),
    [notes, format],
  );

  const activeKey = activeNote
    ? `${activeNote.format}|${activeNote.status}|${activeNote.generated_at ?? ""}|${activeNote.edited_at ?? ""}|${activeNote.signed_at ?? ""}`
    : undefined;
  if (activeKey !== draftSourceKey) {
    setDraftSourceKey(activeKey);
    setDraftSections(activeNote ? activeNote.sections : []);
    setError(null);
  }

  const upsertNote = useCallback((note: ProgressNote) => {
    setNotes((prev) => [...prev.filter((n) => n.format !== note.format), note]);
  }, []);

  const doGenerate = useCallback(
    async (targetLang: "en" | "hu") => {
      if (!sessionId) return;
      const sid = sessionId;
      setBusy(true);
      setError(null);
      try {
        const note = await generate(format, targetLang, promptId);
        if (sid !== sessionIdRef.current) return; // session switched mid-poll — discard
        upsertNote(note);
      } catch (e: any) {
        if (sid !== sessionIdRef.current) return;
        setError(e?.message || String(e));
      } finally {
        if (sid === sessionIdRef.current) setBusy(false);
      }
    },
    [sessionId, generate, format, promptId, upsertNote],
  );

  const requestRegenerate = useCallback(() => {
    if (!activeNote) return;
    if (activeNote.status === "draft" && activeNote.edited_at) {
      setConfirmRegenerate(true);
    } else {
      void doGenerate(activeNote.lang);
    }
  }, [activeNote, doGenerate]);

  const doSave = useCallback(async () => {
    if (!sessionId || !activeNote) return;
    const sid = sessionId;
    setBusy(true);
    setError(null);
    try {
      await saveNote(
        format,
        activeNote.lang,
        draftSections.map((s) => ({ key: s.key, text: s.text })),
      );
      if (sid !== sessionIdRef.current) return;
      upsertNote({ ...activeNote, sections: draftSections, edited_at: Date.now() });
    } catch (e: any) {
      if (sid !== sessionIdRef.current) return;
      setError(e?.message || String(e));
    } finally {
      if (sid === sessionIdRef.current) setBusy(false);
    }
  }, [sessionId, saveNote, format, activeNote, draftSections, upsertNote]);

  const doSign = useCallback(async () => {
    if (!sessionId || !activeNote) return;
    const sid = sessionId;
    setBusy(true);
    setError(null);
    try {
      await signNote(format);
      if (sid !== sessionIdRef.current) return;
      upsertNote({ ...activeNote, status: "signed", signed_at: Date.now() });
    } catch (e: any) {
      if (sid !== sessionIdRef.current) return;
      setError(e?.message || String(e));
    } finally {
      if (sid === sessionIdRef.current) setBusy(false);
    }
  }, [sessionId, signNote, format, activeNote, upsertNote]);

  const doDelete = useCallback(async () => {
    if (!sessionId) return;
    const sid = sessionId;
    setBusy(true);
    setError(null);
    try {
      await deleteNote(format);
      if (sid !== sessionIdRef.current) return;
      setNotes((prev) => prev.filter((n) => n.format !== format));
    } catch (e: any) {
      if (sid !== sessionIdRef.current) return;
      setError(e?.message || String(e));
    } finally {
      if (sid === sessionIdRef.current) setBusy(false);
    }
  }, [sessionId, deleteNote, format]);

  const doCopy = useCallback(() => {
    if (!activeNote) return;
    const sections = activeNote.status === "draft" ? draftSections : activeNote.sections;
    const body = sections.map((s) => `${s.title}\n${s.text}`).join("\n\n");
    const text = `${format.toUpperCase()}\n\n${body}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [activeNote, draftSections, format]);

  const isDraft = activeNote?.status === "draft";
  const isSigned = activeNote?.status === "signed";

  return (
    <div style={styles.root}>
      {error && <div style={styles.error}>{error}</div>}

      {!activeNote && (
        <div style={styles.generateRow}>
          <div style={styles.langRow}>
            <span style={styles.langLabel}>{t("progressNotes.language")}</span>
            <label style={styles.radioLabel}>
              <input type="radio" checked={lang === "en"} onChange={() => setLangOverride("en")} /> English
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" checked={lang === "hu"} onChange={() => setLangOverride("hu")} /> Magyar
            </label>
          </div>
          <Button
            variant="primary"
            onClick={() => void doGenerate(lang)}
            disabled={busy || !sessionId}
          >
            {busy ? t("progressNotes.generating") : t("progressNotes.generate")}
          </Button>
        </div>
      )}

      {activeNote && isDraft && (
        <div>
          <div style={styles.banner}>{t("progressNotes.draftBanner")}</div>
          {draftSections.map((sec, idx) => (
            <div key={sec.key} style={styles.section}>
              <div style={styles.sectionTitle}>{sec.title}</div>
              <textarea
                value={sec.text}
                onChange={(e) => {
                  const val = e.target.value;
                  setDraftSections((prev) => prev.map((s, i) => (i === idx ? { ...s, text: val } : s)));
                }}
                style={styles.textarea}
              />
            </div>
          ))}
          <div style={styles.actionsRow}>
            <Button variant="primary" onClick={() => void doSave()} disabled={busy}>
              {t("progressNotes.save")}
            </Button>
            <Button variant="outline" onClick={requestRegenerate} disabled={busy}>
              {t("progressNotes.regenerate")}
            </Button>
            <Button variant="outline" onClick={() => setConfirmSign(true)} disabled={busy}>
              {t("progressNotes.sign")}
            </Button>
            <Button variant="danger" onClick={() => setConfirmDeleteDraft(true)} disabled={busy}>
              {t("progressNotes.delete")}
            </Button>
            <Button variant="outline" onClick={doCopy}>
              {copied ? t("progressNotes.copied") : t("progressNotes.copy")}
            </Button>
          </div>
        </div>
      )}

      {activeNote && isSigned && (
        <div>
          <div style={styles.signedBanner}>
            {t("progressNotes.signedAt")}
            {activeNote.signed_at ? `: ${new Date(activeNote.signed_at).toLocaleString()}` : ""}
          </div>
          {activeNote.sections.map((sec) => (
            <div key={sec.key} style={styles.section}>
              <div style={styles.sectionTitle}>{sec.title}</div>
              <div style={styles.readonlyText}>{sec.text}</div>
            </div>
          ))}
          <div style={styles.actionsRow}>
            <Button
              variant="danger"
              onClick={() => setConfirmDeleteSigned(true)}
              disabled={busy}
            >
              {t("progressNotes.delete")}
            </Button>
            <Button variant="outline" onClick={doCopy}>
              {copied ? t("progressNotes.copied") : t("progressNotes.copy")}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmSign}
        message={t("progressNotes.signConfirm")}
        confirmLabel={t("progressNotes.sign")}
        onConfirm={() => {
          setConfirmSign(false);
          void doSign();
        }}
        onCancel={() => setConfirmSign(false)}
      />
      <ConfirmDialog
        open={confirmRegenerate}
        message={t("progressNotes.regenerateConfirm")}
        confirmLabel={t("progressNotes.regenerate")}
        onConfirm={() => {
          setConfirmRegenerate(false);
          if (activeNote) void doGenerate(activeNote.lang);
        }}
        onCancel={() => setConfirmRegenerate(false)}
      />
      <ConfirmDialog
        open={confirmDeleteDraft}
        message={t("progressNotes.deleteDraftConfirm")}
        confirmLabel={t("progressNotes.delete")}
        onConfirm={() => {
          setConfirmDeleteDraft(false);
          void doDelete();
        }}
        onCancel={() => setConfirmDeleteDraft(false)}
      />
      <ConfirmDialog
        open={confirmDeleteSigned}
        message={t("progressNotes.deleteSignedConfirm")}
        confirmLabel={t("progressNotes.delete")}
        variant="danger"
        onConfirm={() => {
          setConfirmDeleteSigned(false);
          void doDelete();
        }}
        onCancel={() => setConfirmDeleteSigned(false)}
      />
    </div>
  );
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: 12, padding: 12, overflowY: "auto" } as React.CSSProperties,
  error: { color: "var(--color-danger, #b91c1c)", fontSize: 13, whiteSpace: "pre-wrap" } as React.CSSProperties,
  generateRow: { display: "flex", flexDirection: "column", gap: 12 } as React.CSSProperties,
  langRow: { display: "flex", gap: 12, alignItems: "center" } as React.CSSProperties,
  langLabel: { fontSize: 13, fontWeight: 600 } as React.CSSProperties,
  radioLabel: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 } as React.CSSProperties,
  banner: {
    background: "var(--color-subsurface)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 12,
  } as React.CSSProperties,
  signedBanner: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 12,
    color: "var(--color-muted)",
  } as React.CSSProperties,
  section: { marginBottom: 12 } as React.CSSProperties,
  sectionTitle: { fontSize: 13, fontWeight: 700, marginBottom: 4 } as React.CSSProperties,
  textarea: {
    width: "100%",
    minHeight: 80,
    borderRadius: 8,
    border: "1px solid var(--color-border)",
    padding: 8,
    fontSize: 13,
    fontFamily: "inherit",
  } as React.CSSProperties,
  readonlyText: { fontSize: 13, whiteSpace: "pre-wrap" } as React.CSSProperties,
  actionsRow: { display: "flex", gap: 8, flexWrap: "wrap" } as React.CSSProperties,
};
